import { create } from 'zustand';
import { 
  getInventory, 
  createInventoryItem, 
  updateInventoryItem, 
  deleteInventoryItem,
  type InventoryItem as ApiInventoryItem
} from '@/services/edgeFunctionWrappers/inventoryManagementWrapper';
import { useUserStore } from '@/store/userStore';

export interface InventoryItem {
  id: string;
  name: string;
  size: string;
  stock: number;
  available: number;
  reserved: number;
  price: number;
  category: string;
  lastUpdated: string;
}

interface InventoryState {
  items: InventoryItem[];
  isLoading: boolean;
  error: string | null;
  fetchInventory: () => Promise<void>;
  updateItem: (item: InventoryItem) => Promise<void>;
  addItem: (item: Omit<InventoryItem, 'id' | 'lastUpdated'>) => Promise<void>;
  deleteItem: (id: string) => Promise<void>;
}

// Helper to convert API item to store item
const mapApiItemToStoreItem = (item: ApiInventoryItem): InventoryItem => ({
  id: item.id,
  name: item.name,
  size: item.size ?? '',
  stock: item.stock,
  available: item.available,
  reserved: item.reserved,
  price: item.price,
  category: item.category,
  lastUpdated: item.updated_at ?? new Date().toISOString(),
});

export const useInventoryStore = create<InventoryState>((set, get) => ({
  items: [],
  isLoading: false,
  error: null,

  fetchInventory: async () => {
    const gymId = useUserStore.getState().gymId;
    if (!gymId) {
      // Lecture : on n'interrompt pas le rendu, on affiche juste l'état vide.
      set({ error: 'No gym selected', isLoading: false });
      return;
    }

    set({ isLoading: true, error: null });
    
    const items = await getInventory(gymId);

    if (items === null) {
      set({ error: 'Failed to fetch inventory', isLoading: false });
    } else {
      set({
        items: items.map(mapApiItemToStoreItem),
        isLoading: false,
      });
    }
  },

  updateItem: async (item) => {
    const gymId = useUserStore.getState().gymId;
    if (!gymId) {
      set({ error: 'No gym selected', isLoading: false });
      throw new Error('No gym selected');
    }

    set({ isLoading: true, error: null });

    const updatedItem = await updateInventoryItem(gymId, item.id, {
      name: item.name,
      size: item.size,
      stock: item.stock,
      available: item.available,
      reserved: item.reserved,
      price: item.price,
      category: item.category,
    });

    if (updatedItem === null) {
      // Le wrapper renvoie null au lieu de lever : sans ce throw, l'écran
      // affichait « Article mis à jour » alors que rien n'avait été écrit.
      set({ error: 'Failed to update item', isLoading: false });
      throw new Error('Failed to update item');
    } else {
      const updatedItems = get().items.map(i =>
        i.id === item.id ? mapApiItemToStoreItem(updatedItem) : i
      );
      set({ items: updatedItems, isLoading: false });
    }
  },

  addItem: async (item) => {
    const gymId = useUserStore.getState().gymId;
    if (!gymId) {
      set({ error: 'No gym selected', isLoading: false });
      throw new Error('No gym selected');
    }

    set({ isLoading: true, error: null });

    const newItem = await createInventoryItem(gymId, {
      name: item.name,
      size: item.size,
      stock: item.stock,
      available: item.available,
      reserved: item.reserved,
      price: item.price,
      category: item.category,
    });

    if (newItem === null) {
      set({ error: 'Failed to add item', isLoading: false });
      throw new Error('Failed to add item');
    } else {
      set({
        items: [...get().items, mapApiItemToStoreItem(newItem)],
        isLoading: false,
      });
    }
  },

  deleteItem: async (id) => {
    const gymId = useUserStore.getState().gymId;
    if (!gymId) {
      set({ error: 'No gym selected', isLoading: false });
      throw new Error('No gym selected');
    }

    set({ isLoading: true, error: null });

    const success = await deleteInventoryItem(gymId, id);

    if (!success) {
      set({ error: 'Failed to delete item', isLoading: false });
      throw new Error('Failed to delete item');
    } else {
      const updatedItems = get().items.filter(item => item.id !== id);
      set({ items: updatedItems, isLoading: false });
    }
  }
}));
